/**
 * REST request/response shapes for `/api/auth/*`, `/api/me/*`, `/api/admin/*`
 * and `/api/homes/:home/info`. Errors are always `{ error: string; message?: string; issues?: unknown }`.
 */
import { z } from 'zod';
import { AccessSchedule, Capability, Grant, GrantTarget, Membership, Privilege, Role, SessionInfo, } from './access.js';
import { HomeId } from './model.js';
import { PublicUser } from './protocol.js';
import { GuestStay, HHMM } from './stays.js';
export const LoginInput = z.object({ username: z.string().min(1).max(80), password: z.string().min(1).max(200) });
export const RedeemInput = z.object({ code: z.string().min(4).max(40) });
export const ChangePasswordInput = z.object({ current: z.string().max(200), next: z.string().min(8).max(200) });
/** A grant as sent by the editor; `id` is optional (assigned server-side). */
export const GrantInput = z.object({
    id: z.string().optional(),
    target: GrantTarget,
    capabilities: z.array(Capability).min(1),
    effect: z.enum(['allow', 'deny']).default('allow'),
});
export const MembershipInput = z.object({
    role: Role,
    customRoleId: z.string().nullable().default(null),
    schedule: AccessSchedule.nullable().default(null),
    privileges: z.array(Privilege).default([]),
    grants: z.array(GrantInput).default([]),
});
export const InviteMembershipInput = MembershipInput.extend({ homeId: HomeId });
export const CreateUserInput = z.object({
    username: z.string().min(2).max(40).regex(/^[a-z0-9._-]+$/),
    displayName: z.string().min(1).max(80),
    /** Omit to have the server generate a temporary password (returned once). */
    password: z.string().min(8).max(200).optional(),
    isOwner: z.boolean().default(false),
    /** homeId → membership. */
    memberships: z.record(z.string(), MembershipInput).default({}),
});
export const UpdateUserInput = z.object({
    username: z.string().min(2).max(40).regex(/^[a-z0-9._-]+$/).optional(),
    displayName: z.string().min(1).max(80).optional(),
    disabled: z.boolean().optional(),
    isOwner: z.boolean().optional(),
});
export const ResetPasswordInput = z.object({ password: z.string().min(8).max(200).optional() });
export const BulkMembersInput = z.object({
    userIds: z.array(z.string()).min(1).max(500),
    /** null removes the home from each user. */
    role: Role.nullable(),
    customRoleId: z.string().nullable().default(null),
});
export const CreateInviteInput = z.object({
    guestName: z.string().min(1).max(80),
    note: z.string().max(500).default(''),
    startsAt: z.number().int(), // epoch s
    endsAt: z.number().int(),
    memberships: z.array(InviteMembershipInput).min(1),
});
export const UpdateInviteInput = z.object({
    guestName: z.string().min(1).max(80).optional(),
    note: z.string().max(500).optional(),
    startsAt: z.number().int().optional(),
    endsAt: z.number().int().optional(),
    memberships: z.array(InviteMembershipInput).min(1).optional(),
});
export const CustomRoleInput = z.object({
    name: z.string().min(1).max(60),
    privileges: z.array(Privilege).default([]),
    grants: z.array(GrantInput).default([]),
});
export const CustomRole = z.object({
    id: z.string(),
    homeId: HomeId,
    name: z.string(),
    privileges: z.array(Privilege),
    grants: z.array(Grant),
    memberCount: z.number(),
    createdAt: z.number(),
});
/** Home-agnostic presets applied by the UI into a MembershipInput. */
export const RoleTemplateInput = z.object({
    name: z.string().min(1).max(60),
    description: z.string().max(300).default(''),
    role: z.enum(['admin', 'member', 'guest']),
    privileges: z.array(Privilege).default([]),
    /** Only portable targets make sense here: home / domain / module. */
    grants: z.array(GrantInput).default([]),
    schedule: AccessSchedule.nullable().default(null),
});
export const RoleTemplate = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    role: z.enum(['admin', 'member', 'guest']),
    privileges: z.array(Privilege),
    grants: z.array(Grant),
    schedule: AccessSchedule.nullable(),
    builtin: z.boolean(),
});
/** Membership as seen in the admin UI, with live schedule status. */
export const AdminMembership = Membership.extend({
    inWindow: z.boolean(),
    nextChangeAt: z.number().nullable(),
    customRoleName: z.string().nullable(),
});
export const AdminUser = z.object({
    id: z.string(),
    username: z.string(),
    displayName: z.string(),
    isOwner: z.boolean(),
    disabled: z.boolean(),
    mustChangePassword: z.boolean(),
    isGuest: z.boolean(),
    inviteId: z.string().nullable(),
    createdAt: z.number(),
    lastSeenAt: z.number().nullable(),
    sessionCount: z.number(),
    memberships: z.array(AdminMembership),
});
export const MeHome = z.object({
    homeId: HomeId,
    name: z.string(),
    role: Role,
    isAdmin: z.boolean(),
    inWindow: z.boolean(),
    nextChangeAt: z.number().nullable(),
});
export const MeResponse = z.object({
    user: PublicUser,
    homes: z.array(MeHome),
    /** Homes this user administers (all homes for the owner). */
    adminHomes: z.array(HomeId),
});
export const SessionsResponse = z.object({ sessions: z.array(SessionInfo) });
// ---------- house info ----------
export const InfoContact = z.object({
    id: z.string(),
    name: z.string().max(80),
    role: z.string().max(80),
    phone: z.string().max(40),
    note: z.string().max(200),
});
export const InfoSection = z.object({
    id: z.string(),
    title: z.string().max(80),
    icon: z.string().max(40),
    /** Plain text. Blank lines split paragraphs; lines starting "- " are bullets. */
    body: z.string().max(4000),
    /** `checkout`: shown to guests as the check-out checklist. */
    tag: z.enum(['checkout']).optional(),
});
export const HouseInfo = z.object({
    contacts: z.array(InfoContact).max(12),
    sections: z.array(InfoSection).max(30),
    /** Epoch seconds. */
    updatedAt: z.number().nullable(),
    // Guest stays (all optional, back-compatible):
    /** Default arrival / departure times for new stays, "HH:MM". */
    checkInTime: HHMM.optional(),
    checkOutTime: HHMM.optional(),
    /** Shown to guests before arrival. */
    address: z.string().max(300).optional(),
    arrivalNotes: z.string().max(2000).optional(),
    /** Invite message templates (see DEFAULT_STAY_TEMPLATES for placeholders). */
    messageTemplates: z.object({ sms: z.string().max(1000), email: z.string().max(4000) }).optional(),
});
export const WifiSecurity = z.enum(['WPA', 'WEP', 'nopass']);
/** GET /api/homes/:home/info. Each half is null when the privilege (or data) is missing. */
export const HomeInfoResponse = z.object({
    info: HouseInfo.nullable(),
    wifi: z.object({ ssid: z.string(), password: z.string(), security: WifiSecurity }).nullable(),
});
/**
 * GET /api/homes/:home/stay (guest users): the stay plus house info (from the invite, before arrival too)
 * and Wi-Fi (in the stay window only).
 */
export const GuestStayResponse = HomeInfoResponse.extend({ stay: GuestStay });
/** GET/PUT /api/admin/homes/:home/info. */
export const AdminHomeInfo = z.object({
    info: HouseInfo,
    showWifi: z.boolean(),
    wifiSecurity: WifiSecurity,
    /** Whether WIFI_SSID / WIFI_PASSWORD are configured for this home (secrets never returned here). */
    wifiConfigured: z.boolean(),
});
export const AdminHomeInfoInput = z.object({
    info: HouseInfo.omit({ updatedAt: true }).optional(),
    showWifi: z.boolean().optional(),
    wifiSecurity: WifiSecurity.optional(),
});
//# sourceMappingURL=admin.js.map